import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChild, FaEnvelope, FaPhone, FaSearch, FaUser } from "react-icons/fa";
import { useCan } from "../../../authz/useCan";
import { fetchDashboardQuickSearch } from "../api/dashboard.api";
import type { DashboardQuickSearchContact, DashboardQuickSearchResult } from "../types/dashboard.types";

const SEARCH_LIMIT = 8;
const SEARCH_DELAY_MS = 275;

const ContactLinks = ({ contact }: { contact: Pick<DashboardQuickSearchContact, "phone" | "email"> }) => (
  <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
    {contact.phone ? (
      <a className="inline-flex items-center gap-1 hover:text-indigo-700 hover:underline dark:hover:text-indigo-300" href={`tel:${contact.phone}`}>
        <FaPhone aria-hidden="true" /> {contact.phone}
      </a>
    ) : null}
    {contact.email ? (
      <a className="inline-flex min-w-0 items-center gap-1 hover:text-indigo-700 hover:underline dark:hover:text-indigo-300" href={`mailto:${contact.email}`}>
        <FaEnvelope aria-hidden="true" /> <span className="truncate">{contact.email}</span>
      </a>
    ) : null}
  </span>
);

const ParentCallButton = ({ name, phone }: { name: string; phone?: string }) => {
  const { t } = useTranslation();
  if (!phone) return null;

  return (
    <a
      href={`tel:${phone}`}
      aria-label={t("dashboard.quickSearch.callParent", {
        defaultValue: "Call {{name}}",
        name,
      })}
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-indigo-200 bg-white text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200 dark:hover:bg-white/15"
    >
      <FaPhone aria-hidden="true" />
    </a>
  );
};

const DashboardQuickSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canSearchParents = useCan("family.parents", "list");
  const canSearchChildren = useCan("family.children", "list");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DashboardQuickSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [completedQuery, setCompletedQuery] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const normalizedQuery = query.trim();

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (normalizedQuery.length < 2 || !canSearchParents || !canSearchChildren) {
      setResults([]);
      setLoading(false);
      setError(false);
      setCompletedQuery(null);
      return undefined;
    }

    setResults([]);
    setLoading(true);
    setError(false);
    setCompletedQuery(null);
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const data = await fetchDashboardQuickSearch(normalizedQuery, SEARCH_LIMIT, controller.signal);
        if (requestId !== requestIdRef.current) return;
        setResults(data);
        setCompletedQuery(normalizedQuery);
      } catch (searchError) {
        if (controller.signal.aborted || requestId !== requestIdRef.current) return;
        setResults([]);
        setError(true);
        setCompletedQuery(null);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, SEARCH_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [canSearchChildren, canSearchParents, normalizedQuery]);

  const visible = canSearchParents && canSearchChildren;
  const status = useMemo(() => {
    if (normalizedQuery.length < 2) return null;
    if (loading) return t("dashboard.quickSearch.loading", { defaultValue: "Searching…" });
    if (error) return t("dashboard.quickSearch.error", { defaultValue: "Search is temporarily unavailable." });
    if (completedQuery === normalizedQuery && results.length === 0) {
      return t("dashboard.quickSearch.empty", { defaultValue: "No parents or children found." });
    }
    return null;
  }, [completedQuery, error, loading, normalizedQuery, results.length, t]);

  if (!visible) return null;

  return (
    <section className="dashboard-enter rounded-[24px] border border-slate-200/80 bg-white/95 p-4 shadow-sm dark:border-white/10 dark:bg-slate-950/70 sm:p-5" aria-labelledby="dashboard-quick-search-title">
      <h2 id="dashboard-quick-search-title" className="text-base font-bold text-slate-950 dark:text-white">
        {t("dashboard.quickSearch.title", { defaultValue: "Quick search" })}
      </h2>
      <label className="relative mt-3 block">
        <span className="sr-only">{t("dashboard.quickSearch.placeholder", { defaultValue: "Search parent or child…" })}</span>
        <FaSearch aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("dashboard.quickSearch.placeholder", { defaultValue: "Search parent or child…" })}
          className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10"
        />
      </label>

      <div aria-live="polite" className="mt-3">
        {status ? <p className={`text-sm ${error ? "text-red-600 dark:text-red-300" : "text-slate-500 dark:text-slate-400"}`}>{status}</p> : null}
        {!loading && !error && results.length > 0 ? (
          <ul className="grid gap-2 sm:grid-cols-2" aria-label={t("dashboard.quickSearch.results", { defaultValue: "Search results" })}>
            {results.map((result) => (
              <li key={`${result.type}-${result.id}`} className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5">
                <div className="flex min-w-0 items-start gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(result.type === "PARENT" ? `/family/parent/${result.id}/details` : `/family/children/${result.id}/details`)}
                    className="flex min-w-0 flex-1 items-start gap-3 rounded-xl text-left focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <span className="mt-0.5 shrink-0 text-indigo-600 dark:text-indigo-300">{result.type === "PARENT" ? <FaUser aria-hidden="true" /> : <FaChild aria-hidden="true" />}</span>
                    <span className="min-w-0">
                      <span className="block text-xs font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                        {result.type === "PARENT"
                          ? t("dashboard.quickSearch.parent", { defaultValue: "Parent" })
                          : t("dashboard.quickSearch.child", { defaultValue: "Child" })}
                      </span>
                      <span className="block truncate font-bold text-slate-950 dark:text-white">{result.displayName}</span>
                    </span>
                  </button>
                  {result.type === "PARENT" ? <ParentCallButton name={result.displayName} phone={result.phone} /> : null}
                </div>
                <div className="ml-7 mt-2 space-y-2">
                  {result.type === "PARENT" ? (
                    <>
                      <ContactLinks contact={result} />
                      {result.childNames.length ? <p className="text-xs text-slate-500 dark:text-slate-400">{t("dashboard.quickSearch.children", { defaultValue: "Children" })}: {result.childNames.join(", ")}</p> : null}
                    </>
                  ) : result.parents.length ? (
                    <div className="divide-y divide-slate-200/80 dark:divide-white/10">
                      {result.parents.map((parent, index) => (
                        <div key={parent.id || `${parent.displayName}-${index}`} className="py-2 first:pt-0 last:pb-0">
                          <div className="flex min-w-0 items-center gap-2">
                            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700 dark:text-slate-200">{parent.displayName}</p>
                            <ParentCallButton name={parent.displayName} phone={parent.phone} />
                          </div>
                          <div className="mt-1">
                            <ContactLinks contact={parent} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-slate-500 dark:text-slate-400">{t("dashboard.quickSearch.noContact", { defaultValue: "No parent contact details available." })}</p>}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
};

export default DashboardQuickSearch;
